import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import { fetchEditableContact } from 'flow/actions/contact-actions';
import ContactEditFields from 'flow/components/contact-components/ContactEditFields';
import Page from 'flow/components/Setting/Page';
import SettingsBlock from 'flow/components/settings/SettingsBlock';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { getUserCountryCode } from 'flow/selectors/selectors';
import { routes } from 'flow/types/routes';

import { getContactAvatar } from 'flow/utils';
import { generatePath } from 'flow/utils/router-utils';
import Icon from './Icon';
import IconButton from './IconButton';
import Loading from './Loading';
import UserAvatar from './UserAvatar';
import { ExtensionKey } from 'flow/types/EntityKeys';

const SettingsContactEdit = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { contactKey } = useContact(extensionKey);

    const contact = useAppSelector((state) =>
        state.contacts.getIn(['flowContacts', contactKey]),
    );

    const userCountryCode = useAppSelector((state) =>
        getUserCountryCode(state, extensionKey),
    );

    useEffect(() => {
        dispatch(fetchEditableContact(contactKey));
    }, [contactKey]);

    const openEditModal = () => {
        openModal('editContact', {
            contact,
            isEditingOwnContact: true,
        });
    };

    if (!contact?.get('editableContactDTO')) {
        return <Loading />;
    }

    return (
        <Page
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.account'], { extensionKey }),
                )
            }
            header="Settings.Account.Contact"
            action={
                <IconButton
                    onClick={openEditModal}
                    aria-label={i18next.t('Tooltip.Nav.Title.Edit')}
                >
                    <Icon iconKey="edit" />
                </IconButton>
            }
        >
            <SettingsBlock className="flex flex-col gap-y-6">
                <UserAvatar
                    size="large"
                    src={getContactAvatar(contact?.get('editableContactDTO'))}
                />
                <ContactEditFields
                    contactFields={contact
                        ?.get('editableContactDTO')
                        ?.get('contactFields')}
                    userCountryCode={userCountryCode}
                    disableEdit={true}
                />
            </SettingsBlock>
        </Page>
    );
};

export default SettingsContactEdit;
